/**
 * Hook useDirectMessages
 * Gestisce la chat diretta tra utenti (desktop↔desktop, desktop↔mobile, mobile↔mobile)
 * Usa WebSocket CONDIVISO per real-time + REST API per persistenza
 *
 * IMPORTANTE: Questo hook usa useSharedWebSocket per condividere la connessione
 * con il sistema foto, evitando conflitti WebSocket.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSharedWebSocket, sendSharedMessage, isSharedWsConnected } from './useSharedWebSocket';
import { playFullNotification } from '../utils/audioNotifications';

// Types
export interface DMUser {
  userId: string;
  userName: string;
  userType: 'desktop' | 'mobile';
  online?: boolean;
  status?: 'online' | 'offline' | 'away';
  email?: string;
}

export interface DMMessage {
  id?: number;
  messageId?: number;
  fromUserId: string;
  fromUserName: string;
  fromUserType: 'desktop' | 'mobile';
  toUserId: string;
  toUserName: string;
  toUserType: 'desktop' | 'mobile';
  text?: string;
  message?: string;
  messageType?: 'text' | 'image' | 'file' | 'system' | 'photo_request';
  timestamp?: string;
  created_at?: string;
  read_at?: string | null;
  // Campi per richiesta foto (quando messageType === 'photo_request')
  isPhotoRequest?: boolean;
  requestId?: string;
  photoRequestData?: {
    requestId: string;
    title: string;
    description?: string;
    fromUserId: string;
    fromUserName: string;
    status: 'pending' | 'accepted' | 'refused' | 'completed';
    photoCount?: number;  // Numero di foto richieste
  };
}

export interface DMConversation {
  id: number;
  other_user_id: string;
  other_user_name: string;
  other_user_type: 'desktop' | 'mobile';
  other_user_status?: 'online' | 'offline' | 'away';
  other_user_last_seen?: string;
  last_message_preview?: string;
  last_message_at?: string;
  unread_count: number;
}

interface UseDirectMessagesProps {
  userId: string;
  userName: string;
  userType: 'desktop' | 'mobile';
  autoConnect?: boolean;
}

interface UseDirectMessagesReturn {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;

  // Data
  conversations: DMConversation[];
  messages: DMMessage[];
  onlineUsers: DMUser[];
  allUsers: DMUser[];
  totalUnreadCount: number;
  lastReceivedMessage: DMMessage | null; // Ultimo messaggio ricevuto (per notifiche)

  // Current chat
  activeChat: DMUser | null;
  typingUsers: Map<string, string>; // userId -> userName

  // Actions
  connect: () => void;
  disconnect: () => void;
  sendMessage: (toUser: DMUser, text: string) => Promise<void>;
  loadConversations: () => Promise<void>;
  loadMessages: (otherUserId: string, limit?: number) => Promise<void>;
  loadAllUsers: () => Promise<void>;
  markAsRead: (otherUserId: string) => Promise<void>;
  setActiveChat: (user: DMUser | null) => void;
  sendTypingIndicator: (toUser: DMUser, isTyping: boolean) => void;
  clearMessages: () => void;
  removeMessage: (messageId: string | number) => void;
}

// API base URL
const API_BASE = '/api/dm';

// Le fetch abortite da smontaggio/navigazione ("Failed to fetch"/AbortError) non sono
// errori: loggarle come tali sporca la console e maschera i problemi veri (emerse
// nell'audit browser 2026-07-15 su /profile).
const isAbort = (err: unknown): boolean => {
  const msg = String((err as Error)?.message || err || '');
  return (err as Error)?.name === 'AbortError' || msg.includes('Failed to fetch') || msg.includes('aborted');
};

export function useDirectMessages({
  userId,
  userName,
  userType,
  autoConnect = true
}: UseDirectMessagesProps): UseDirectMessagesReturn {

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [conversations, setConversations] = useState<DMConversation[]>([]);
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<DMUser[]>([]);
  const [allUsers, setAllUsers] = useState<DMUser[]>([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [lastReceivedMessage, setLastReceivedMessage] = useState<DMMessage | null>(null);

  // Chat state
  const [activeChat, setActiveChat] = useState<DMUser | null>(null);
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());

  // Refs
  const typingTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const handlersRegistered = useRef(false);

  // Use shared WebSocket
  const {
    isConnected: _wsConnected,
    isConnecting: wsConnecting,
    dmAuthenticated,
    registerHandler,
    send: _wsSend
  } = useSharedWebSocket({
    userId,
    userName,
    userType,
    enabled: autoConnect
  });

  // Connect - now handled by useSharedWebSocket
  const connect = useCallback(() => {
    // Connection is managed by useSharedWebSocket
    console.log('[DM Hook] Connect called - using shared WebSocket');
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data: any) => {
    console.log('[DM Hook] Received:', data.type);

    switch (data.type) {
      case 'dm_auth_success':
        console.log('[DM Hook] Authenticated successfully via shared WebSocket');
        setIsConnected(true);
        setIsConnecting(false);

        // Load initial data
        loadConversations();
        loadAllUsers();
        break;

      case 'dm_new_message':
        // New message received
        const newMsg: DMMessage = {
          id: data.messageId,
          messageId: data.messageId,
          fromUserId: data.fromUserId,
          fromUserName: data.fromUserName,
          fromUserType: data.fromUserType,
          toUserId: data.toUserId,
          toUserName: data.toUserName,
          toUserType: data.toUserType,
          text: data.text,
          messageType: data.messageType || 'text',
          timestamp: data.timestamp,
          // Campi per richieste foto
          isPhotoRequest: data.isPhotoRequest,
          requestId: data.requestId,
          photoRequestData: data.photoRequestData
        };

        // SEMPRE aggiorna lastReceivedMessage per notifiche
        // (anche se non c'è activeChat aperta)
        setLastReceivedMessage(newMsg);

        // Add to messages if from/to active chat
        if (activeChat &&
            (data.fromUserId === activeChat.userId || data.toUserId === activeChat.userId)) {
          setMessages(prev => [...prev, newMsg]);
        }

        // Update unread count + audio notification per messaggio in arrivo
        if (data.toUserId === userId) {
          setTotalUnreadCount(prev => prev + 1);

          // Reload conversations to update preview
          loadConversations();

          // Audio notification per ogni messaggio ricevuto (non solo chat aperta)
          // Suona solo se NON è il proprio messaggio echo
          if (data.fromUserId !== userId) {
            const notifType = (data.messageType === 'photo_request' || data.isPhotoRequest) ? 'photo_request' : 'chat';
            playFullNotification(notifType).catch(err => {
              console.log('[DM Hook] Audio notification error:', err);
            });
          }
        }
        break;

      case 'dm_message_sent':
        // Confirmation of sent message
        const sentMsg: DMMessage = {
          id: data.messageId,
          messageId: data.messageId,
          fromUserId: data.fromUserId,
          fromUserName: data.fromUserName,
          fromUserType: data.fromUserType,
          toUserId: data.toUserId,
          toUserName: data.toUserName,
          toUserType: data.toUserType,
          text: data.text,
          messageType: data.messageType || 'text',
          timestamp: data.timestamp
        };

        // Add to messages
        setMessages(prev => [...prev, sentMsg]);
        break;

      case 'dm_typing':
        // Typing indicator
        const typingKey = `${data.fromUserType}-${data.fromUserId}`;

        if (data.isTyping) {
          setTypingUsers(prev => {
            const next = new Map(prev);
            next.set(typingKey, data.fromUserName);
            return next;
          });

          // Clear typing after 3 seconds
          const existingTimeout = typingTimeoutsRef.current.get(typingKey);
          if (existingTimeout) clearTimeout(existingTimeout);

          typingTimeoutsRef.current.set(typingKey, setTimeout(() => {
            setTypingUsers(prev => {
              const next = new Map(prev);
              next.delete(typingKey);
              return next;
            });
          }, 3000));
        } else {
          setTypingUsers(prev => {
            const next = new Map(prev);
            next.delete(typingKey);
            return next;
          });
        }
        break;

      case 'dm_messages_read':
        // L'altro utente ha letto: spunte blu LIVE sui nostri messaggi verso di lui
        console.log(`[DM Hook] ${data.byUserName} has read messages`);
        setMessages(prev => prev.map(m =>
          String(m.toUserId) === String(data.byUserId) && !m.read_at
            ? { ...m, read_at: new Date().toISOString() }
            : m
        ));
        break;

      case 'dm_user_online':
        // User came online
        setOnlineUsers(prev => {
          const exists = prev.find(u => u.userId === data.userId && u.userType === data.userType);
          if (exists) {
            return prev.map(u =>
              u.userId === data.userId && u.userType === data.userType
                ? { ...u, online: true, status: 'online' }
                : u
            );
          }
          return [...prev, {
            userId: data.userId,
            userName: data.userName,
            userType: data.userType,
            online: true,
            status: 'online'
          }];
        });

        // Update allUsers status
        setAllUsers(prev => prev.map(u =>
          u.userId === data.userId && u.userType === data.userType
            ? { ...u, online: true, status: 'online' }
            : u
        ));

        // Update conversations.other_user_status real-time
        setConversations(prev => prev.map(c =>
          String(c.other_user_id) === String(data.userId) && c.other_user_type === data.userType
            ? { ...c, other_user_status: 'online' }
            : c
        ));
        break;

      case 'dm_user_offline':
        // User went offline
        setOnlineUsers(prev => prev.filter(u =>
          !(u.userId === data.userId && u.userType === data.userType)
        ));

        // Update allUsers status
        setAllUsers(prev => prev.map(u =>
          u.userId === data.userId && u.userType === data.userType
            ? { ...u, online: false, status: 'offline' }
            : u
        ));

        // Update conversations.other_user_status real-time
        setConversations(prev => prev.map(c =>
          String(c.other_user_id) === String(data.userId) && c.other_user_type === data.userType
            ? { ...c, other_user_status: 'offline' }
            : c
        ));
        break;

      case 'dm_users_list':
        // List of connected users
        setOnlineUsers(data.users.map((u: any) => ({
          ...u,
          online: true,
          status: 'online'
        })));
        break;

      case 'message_deleted':
        // A message was deleted - remove from local state
        console.log('[DM Hook] Message deleted:', data.messageId);
        setMessages(prev => prev.filter(m =>
          m.id !== data.messageId && m.messageId !== data.messageId
        ));
        break;

      // === PHOTO REQUEST STATUS UPDATES ===
      case 'request_accepted_by_mobile':
      case 'photo_accept_request':
      case 'photo_request_accepted':  // ✅ Evento inviato dal backend via DM
        // Mobile ha accettato la richiesta foto
        console.log('[DM Hook] 📸 Photo request accepted:', data.requestId);
        setMessages(prev => prev.map(m => {
          if (m.isPhotoRequest && m.requestId === data.requestId && m.photoRequestData) {
            return {
              ...m,
              photoRequestData: { ...m.photoRequestData, status: 'accepted' as const }
            };
          }
          return m;
        }));
        break;

      case 'request_refused_by_mobile':
      case 'photo_refuse_request':
      case 'photo_request_refused':  // ✅ Evento inviato dal backend via DM
        // Mobile ha rifiutato la richiesta foto
        console.log('[DM Hook] 📸 Photo request refused:', data.requestId);
        setMessages(prev => prev.map(m => {
          if (m.isPhotoRequest && m.requestId === data.requestId && m.photoRequestData) {
            return {
              ...m,
              photoRequestData: { ...m.photoRequestData, status: 'refused' as const }
            };
          }
          return m;
        }));
        break;

      case 'request_completed_by_mobile':
      case 'photo_complete_request':
        // Mobile ha completato la richiesta foto
        console.log('[DM Hook] 📸 Photo request completed:', data.requestId);
        setMessages(prev => {
          let matchFound = false;
          const updated = prev.map(m => {
            // ✅ Matching robusto: controlla requestId sia a livello message che photoRequestData
            const msgRequestId = m.requestId || m.photoRequestData?.requestId;
            const isMatch = m.isPhotoRequest && msgRequestId === data.requestId && m.photoRequestData;

            if (isMatch && m.photoRequestData) {
              matchFound = true;
              console.log('[DM Hook] ✅ Match trovato! Aggiornamento status a completed per msgId:', m.id);
              return {
                ...m,
                photoRequestData: { ...m.photoRequestData, status: 'completed' as const }
              };
            }
            return m;
          });

          if (!matchFound) {
            console.log('[DM Hook] ⚠️ Nessun match per requestId:', data.requestId, 'Messages:', prev.map(m => ({
              id: m.id,
              isPhotoRequest: m.isPhotoRequest,
              requestId: m.requestId,
              photoRequestDataRequestId: m.photoRequestData?.requestId
            })));
          }

          return updated;
        });
        break;

      case 'error':
        console.error('[DM Hook] Server error:', data.error);
        setError(data.error);
        break;
    }
  }, [activeChat, userId]);

  // Disconnect - connection is managed by shared WebSocket
  const disconnect = useCallback(() => {
    // Clear typing timeouts
    typingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    typingTimeoutsRef.current.clear();
    setIsConnected(false);
  }, []);

  // REST API: Load conversations
  const loadConversations = useCallback(async () => {
    // Guard: skip if userId is not set (avoids 400 error)
    if (!userId) {
      console.log('[DM Hook] Skipping loadConversations - userId not set');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/conversations`, {
        headers: {
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': userType
        }
      });

      if (!response.ok) throw new Error('Failed to load conversations');

      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations || []);
      }
    } catch (err) {
      if (!isAbort(err)) console.error('[DM Hook] Error loading conversations:', err);
    }
  }, [userId, userName, userType]);

  // REST API: Load messages with user
  const loadMessages = useCallback(async (otherUserId: string, limit = 50) => {
    // Guard: skip if userId is not set
    if (!userId) {
      console.log('[DM Hook] Skipping loadMessages - userId not set');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/messages/${otherUserId}?limit=${limit}`, {
        headers: {
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': userType
        }
      });

      if (!response.ok) throw new Error('Failed to load messages');

      const data = await response.json();
      if (data.success) {
        // Transform API response to our format
        const transformedMessages: DMMessage[] = (data.messages || []).map((m: any) => ({
          id: m.id,
          messageId: m.id,
          fromUserId: m.fromUserId || m.from_user_id,
          fromUserName: m.fromUserName || m.from_user_name,
          fromUserType: m.fromUserType || m.from_user_type,
          toUserId: m.toUserId || m.to_user_id,
          toUserName: m.toUserName || m.to_user_name,
          toUserType: m.toUserType || m.to_user_type,
          text: m.text || m.message,
          messageType: m.messageType || m.message_type,
          timestamp: m.timestamp || m.created_at,
          read_at: m.read_at,
          // Campi per richieste foto
          isPhotoRequest: m.isPhotoRequest,
          requestId: m.requestId,
          photoRequestData: m.photoRequestData
        }));

        setMessages(transformedMessages);
      }
    } catch (err) {
      if (!isAbort(err)) console.error('[DM Hook] Error loading messages:', err);
    }
  }, [userId, userName, userType]);

  // REST API: Load all users
  const loadAllUsers = useCallback(async () => {
    // Guard: skip if userId is not set
    if (!userId) {
      console.log('[DM Hook] Skipping loadAllUsers - userId not set');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/all-users`, {
        headers: {
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': userType
        }
      });

      if (!response.ok) throw new Error('Failed to load users');

      const data = await response.json();
      if (data.success) {
        const users: DMUser[] = (data.users || []).map((u: any) => ({
          userId: u.user_id,
          userName: u.user_name,
          userType: u.user_type,
          email: u.email,
          online: u.online || false,
          status: u.status || 'offline'
        }));

        // Filter out self
        setAllUsers(users.filter(u => !(u.userId === userId && u.userType === userType)));
      }
    } catch (err) {
      if (!isAbort(err)) console.error('[DM Hook] Error loading users:', err);
    }
  }, [userId, userName, userType]);

  // REST API: Mark as read
  const markAsRead = useCallback(async (otherUserId: string) => {
    try {
      // Send via shared WebSocket for real-time
      if (isSharedWsConnected()) {
        sendSharedMessage({
          type: 'dm_read',
          fromUserId: otherUserId,
          fromUserType: activeChat?.userType || 'desktop'
        });
      }

      // Also call REST API
      const response = await fetch(`${API_BASE}/read/${otherUserId}`, {
        method: 'PUT',
        headers: {
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': userType
        }
      });

      if (response.ok) {
        // Reload unread count
        const countResponse = await fetch(`${API_BASE}/unread-count`, {
          headers: { 'x-user-id': userId }
        });

        if (countResponse.ok) {
          const countData = await countResponse.json();
          setTotalUnreadCount(countData.unreadCount || 0);
        }

        // Update conversation unread count
        setConversations(prev => prev.map(c =>
          c.other_user_id === otherUserId ? { ...c, unread_count: 0 } : c
        ));
      }
    } catch (err) {
      if (!isAbort(err)) console.error('[DM Hook] Error marking as read:', err);
    }
  }, [userId, userName, userType, activeChat]);

  // Send message via shared WebSocket or REST fallback
  const sendMessage = useCallback(async (toUser: DMUser, text: string) => {
    console.log('[DM Hook] 📤 sendMessage chiamato:', { to: toUser.userName, wsConnected: isSharedWsConnected() });

    // Try WebSocket first
    if (isSharedWsConnected()) {
      const sent = sendSharedMessage({
        type: 'dm_message',
        toUserId: toUser.userId,
        toUserName: toUser.userName,
        toUserType: toUser.userType,
        text,
        messageType: 'text'
      });

      if (sent) {
        console.log('[DM Hook] ✅ Message sent via shared WebSocket');
        return;
      }
      console.log('[DM Hook] ⚠️ WebSocket send returned false');
    } else {
      console.log('[DM Hook] ⚠️ WebSocket NOT connected, using REST fallback');
    }

    // Fallback to REST API
    console.log('[DM Hook] 📡 Sending via REST API...');
    try {
      const response = await fetch(`${API_BASE}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': userType
        },
        body: JSON.stringify({
          toUserId: toUser.userId,
          toUserName: toUser.userName,
          toUserType: toUser.userType,
          message: text,
          messageType: 'text'
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      if (data.success && data.message) {
        // Add message locally
        const sentMsg: DMMessage = {
          id: data.message.id,
          messageId: data.message.id,
          fromUserId: userId,
          fromUserName: userName,
          fromUserType: userType,
          toUserId: toUser.userId,
          toUserName: toUser.userName,
          toUserType: toUser.userType,
          text: data.message.message,
          messageType: 'text',
          timestamp: data.message.created_at
        };
        setMessages(prev => [...prev, sentMsg]);
      }
    } catch (err) {
      console.error('[DM Hook] Error sending via REST:', err);
      throw err;
    }
  }, [userId, userName, userType]);

  // Send typing indicator via shared WebSocket
  const sendTypingIndicator = useCallback((toUser: DMUser, isTyping: boolean) => {
    if (isSharedWsConnected()) {
      sendSharedMessage({
        type: 'dm_typing',
        toUserId: toUser.userId,
        toUserType: toUser.userType,
        isTyping
      });
    }
  }, []);

  // Clear messages (soft delete per l'utente corrente - i messaggi rimangono visibili all'altro utente)
  const clearMessages = useCallback(async () => {
    if (!activeChat) {
      // Se non c'è chat attiva, pulisci solo localmente
      setMessages([]);
      setLastReceivedMessage(null);
      return;
    }

    try {
      // Chiama backend per soft delete
      const response = await fetch(`${API_BASE}/clear/${activeChat.userId}`, {
        method: 'DELETE',
        headers: {
          'x-user-id': userId,
          'x-user-name': userName,
          'x-user-type': userType
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`[DM Hook] Messaggi cancellati permanentemente: ${data.clearedCount}`);

        // Ricarica la lista conversazioni per aggiornare la preview
        // (il backend ha già aggiornato last_message_preview nel database)
        await loadConversations();
      } else {
        console.error('[DM Hook] Errore cancellazione messaggi:', await response.text());
      }
    } catch (err) {
      console.error('[DM Hook] Errore chiamata API clear messages:', err);
    }

    // Pulisci anche localmente
    setMessages([]);
    setLastReceivedMessage(null);
  }, [activeChat, userId, userName, userType, loadConversations]);

  // Remove a single message from local state (called after successful API delete)
  const removeMessage = useCallback((messageId: string | number) => {
    console.log('[DM Hook] Removing message locally:', messageId);
    setMessages(prev => prev.filter(m =>
      m.id !== messageId && m.messageId !== messageId &&
      m.id !== Number(messageId) && m.messageId !== Number(messageId)
    ));
  }, []);

  // Handle active chat change
  const handleSetActiveChat = useCallback((user: DMUser | null) => {
    setActiveChat(user);

    if (user) {
      // Load messages for this user
      loadMessages(user.userId);

      // Mark as read
      markAsRead(user.userId);
    } else {
      setMessages([]);
    }
  }, [loadMessages, markAsRead]);

  // Sync connection state with shared WebSocket
  useEffect(() => {
    setIsConnecting(wsConnecting);
  }, [wsConnecting]);

  // Register message handlers on shared WebSocket
  useEffect(() => {
    if (handlersRegistered.current) return;

    // Register handlers for all DM message types
    const dmMessageTypes = [
      'dm_auth_success',
      'dm_new_message',
      'dm_message_sent',
      'dm_typing',
      'dm_messages_read',
      'dm_user_online',
      'dm_user_offline',
      'dm_users_list',
      'message_deleted',
      // Photo request status updates
      'request_accepted_by_mobile',
      'photo_accept_request',
      'photo_request_accepted',  // ✅ Evento backend DM
      'request_refused_by_mobile',
      'photo_refuse_request',
      'photo_request_refused',   // ✅ Evento backend DM
      'request_completed_by_mobile',
      'photo_complete_request',
      'error'
    ];

    const unsubscribers = dmMessageTypes.map(type =>
      registerHandler(type, handleWebSocketMessage)
    );

    handlersRegistered.current = true;

    return () => {
      unsubscribers.forEach(unsub => unsub());
      handlersRegistered.current = false;
    };
  }, [registerHandler, handleWebSocketMessage]);

  // Load initial data when dmAuthenticated (anche dopo reconnect: recupera messaggi persi durante offline)
  useEffect(() => {
    if (dmAuthenticated && autoConnect && userId) {
      console.log('[DM Hook] DM authenticated, loading initial data');
      setIsConnected(true);
      loadConversations();
      loadAllUsers();
      // Se c'è una chat attiva, ricarica i messaggi — potrebbero essercene di nuovi arrivati mentre eravamo offline
      if (activeChat?.userId) {
        loadMessages(activeChat.userId);
      }
    }
  }, [dmAuthenticated, autoConnect, userId, loadConversations, loadAllUsers, loadMessages, activeChat?.userId]);

  // Al ritorno in foreground (app riaperta da background): ricarica conversazioni e messaggi attivi
  // Serve su iOS dove l'app può stare aperta ma WS disconnesso in background
  useEffect(() => {
    if (!userId) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        console.log('[DM Hook] 👁️ App tornata visibile, ricarico conversazioni + messaggi attivi');
        loadConversations();
        if (activeChat?.userId) loadMessages(activeChat.userId);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [userId, loadConversations, loadMessages, activeChat?.userId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear typing timeouts
      typingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      typingTimeoutsRef.current.clear();
    };
  }, []);

  // Load initial unread count
  useEffect(() => {
    if (userId) {
      fetch(`${API_BASE}/unread-count`, {
        headers: { 'x-user-id': userId }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setTotalUnreadCount(data.unreadCount || 0);
          }
        })
        .catch(console.error);
    }
  }, [userId]);

  return {
    // Connection state
    isConnected,
    isConnecting,
    error,

    // Data
    conversations,
    messages,
    onlineUsers,
    allUsers,
    totalUnreadCount,
    lastReceivedMessage,

    // Current chat
    activeChat,
    typingUsers,

    // Actions
    connect,
    disconnect,
    sendMessage,
    loadConversations,
    loadMessages,
    loadAllUsers,
    markAsRead,
    setActiveChat: handleSetActiveChat,
    sendTypingIndicator,
    clearMessages,
    removeMessage
  };
}

export default useDirectMessages;
